import { Injectable, Injector } from '@angular/core';
import { GenericPrintService } from '@blcloud/bl-ng-share-module';
import { Observable } from 'rxjs/Observable';
import { map } from 'rxjs/operators';
import { of as ObservableOf } from 'rxjs/observable/of';
import { IReportLayoutSetting, IItemServiceSetting, IReserveSearchCondition } from '@blcloud/bl-datamodel';
import { ReserveSearchReportType } from '@blcloud/bl-datamodel/enum/reserve/reserve-search-report-type';
import { CompanyConst } from '@blcloud/bl-datamodel/const/company';
import { LogicalDeleteDiv } from '@blcloud/bl-datamodel/enum/common/logical-delete-div';
import { ReserveSearchStatus } from '@blcloud/bl-datamodel/enum/reserve/reserve-search-status';
import { ReserveSearchSortOrder } from '@blcloud/bl-datamodel/enum/reserve/reserve-search-sort-order';
import { CarEnterDiv } from '@blcloud/bl-datamodel/enum/deals/car-enter-div';
import { CancelTargetDiv } from '@blcloud/bl-datamodel/enum/reserve/cancel-target-div';
import { ReserveTallyUnitDiv } from '@blcloud/bl-datamodel/enum/reserve/reserve-tally-unit-div';
import { NumberUtils, DateTimeUtils } from '@blcloud/bl-common';
import { ItemServiceSettingResource, ReserveSearchConditionResource } from '@blcloud/bl-ng-resource';
import { BlConditionType } from '@blcloud/bl-ng-common';
import { Rules } from '@blcloud/bl-ng-ui-component';
import { IReservePrintModalData } from './reserve-print-modal.component';
import { RESERVE_PRINT_MODAL_DEFINE } from './reserve-print-modal.define';
import * as moment from 'moment';
import {
  filter as _filter,
  map as _map,
  concat as _concat,
  cloneDeep as _cloneDeep,
  isNil as _isNil,
  split as _split,
  last as _last,
  includes as _includes
} from 'lodash';

/**
 * 予約印刷サービス
 */
@Injectable()
export class ReservePrintModalService extends GenericPrintService {

  /** 商品サービス設定リソース */
  private itemServiceSettingResource: ItemServiceSettingResource;

  /** 予約検索条件リソース */
  private reserveSearchConditionResource: ReserveSearchConditionResource;

  /**
   * コンストラクタ
   * @param injector Injector
   */
  constructor(injector: Injector) {
    super(injector);
    this.itemServiceSettingResource = injector.get(ItemServiceSettingResource);
    this.reserveSearchConditionResource = injector.get(ReserveSearchConditionResource);
  }

  /**
   * @override
   */
  searchReportLayoutSettingList(data: IReservePrintModalData): Observable<IReportLayoutSetting[]> {
    // reportUseCode (帳票用途コード) が利用されていないので仕方なくオーバーライドして自前でフィルタ
    return super.searchReportLayoutSettingList(data).pipe(
      map(
        reportLayoutSettingList => {
          return _filter(reportLayoutSettingList, reportLayoutSetting => {
            return reportLayoutSetting.reportId === this.getReportId(data.reserveSearchReportType);
          });
        }
      )
    );
  }

  /**
   * モーダル起動パラメータから予約検索条件を取得します。
   * @param data 予約印刷モーダル起動パラメータ
   * @returns 予約検索条件
   */
  getReserveSearchCondition(data: IReservePrintModalData): Observable<IReserveSearchCondition> {

    if (_isNil(data)) {
      return ObservableOf({});
    }

    // パラメータで渡された場合はそのまま返す
    const condition = _cloneDeep(data.reserveSearchCondition);
    if (!_isNil(condition)) {
      return ObservableOf(condition);
    }

    // パラメータで渡されなかった場合はデフォルト値を設定して返す
    return this.getLoginOrgCodeIfNeeded().pipe(
      map(
        organizationCode => {

          const lastMonth = moment().subtract(1, 'M');
          const searchStartDate = lastMonth.clone().startOf('M').format(DateTimeUtils.formatString.date); // 先月1日
          const searchEndDate = lastMonth.clone().endOf('M').format(DateTimeUtils.formatString.date);     // 先月末日

          return {
            reserveSearchReportType: data.reserveSearchReportType,
            organizationCode,
            searchStartDate,
            searchEndDate,
            itemServiceNum: 0,
            reserveSearchSortOrder: ReserveSearchSortOrder.TakeOverDateAsc,
            reserveSearchStatus: ReserveSearchStatus.All,
            carEnterDiv: CarEnterDiv.All,
            cancelTargetDiv: CancelTargetDiv.NoCancelData,
            reserveTallyUnitDiv: ReserveTallyUnitDiv.ItemService
          };
        }
      )
    );
  }

  /**
   * 予約検索条件の入力検証ルールを取得します。
   * ※画面で利用する検索開始日と検索終了日のルールだけを取得します。
   * @returns 入力検証ルール
   */
  getReserveSearchConditionRules(): Observable<Rules[]> {
    return this.reserveSearchConditionResource.getRules<Rules[]>().pipe(
      map(
        rules => {
          // 利用する検証ルールからクラス名なしの純粋なプロパティ名を取得
          const usePropertyList = _map(RESERVE_PRINT_MODAL_DEFINE.USE_RULE_KEY, value => {
            return _last(_split(value, '#'));
          });

          // 利用する検証ルールだけ取得
          const propertySchemaDefines = _filter(rules[0].propertySchemaDefines, define => {
            return _includes(usePropertyList, define.propertyId);
          });

          // 必須に変更
          rules[0].propertySchemaDefines = _map(propertySchemaDefines, define => {
            define.requiredInputDiv = true;
            return define;
          });

          return rules;
        }
      )
    );
  }

  /**
   * 商品サービス設定リストを取得します。
   * @returns 商品サービス設定リスト
   */
  searchItemServicesettingList(): Observable<IItemServiceSetting[]> {
    const conditions = [
      { key: 'organizationCode', value: CompanyConst.ORGANIZATION_CODE_ALL_COMPANY, type: BlConditionType.Equal },
      { key: 'logicalDeleteDiv', value: LogicalDeleteDiv.Valid, type: BlConditionType.Equal }
    ];

    const sortFields = [{ field: 'itemServiceDispOrder', desc: false }];

    return this.itemServiceSettingResource.search<IItemServiceSetting>({ conditions, sortFields }).pipe(
      map(
        response => {
          // 先頭に「全て」を入れる
          return _concat(
            [{ itemServiceNum: 0, itemServiceName: '全て' }],
            response.searchResultList
          );
        }
      )
    );
  }

  /**
   * 予約検索帳票タイプから帳票IDを取得します。
   * @param reserveListType 予約検索帳票タイプ
   * @returns 帳票ID
   */
  protected getReportId(reserveSearchReportType: string): string {
    // 「予約台数実績集計」の場合は、予約検索帳票タイプに１を加算して返却する
    if (reserveSearchReportType === ReserveSearchReportType.NumberCarsSummary) {
      return `R-Reserve-00${NumberUtils.parseNumber(reserveSearchReportType) + 1}0`;
    } else {
      return `R-Reserve-00${reserveSearchReportType}0`;
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/feature/reserve-print-modal/reserve-print-modal.service.ts